import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import useLocalSearchShortcut from "../../shared/hooks/useLocalSearchShortcut";
import {
  Badge,
  Button,
  CellText,
  Columns03,
  Copy01,
  DotsVertical,
  EmptyState,
  FilterBar,
  Mail01,
  Menu,
  MessageSquare01,
  Modal,
  PageHeader,
  Pagination,
  Plus,
  SearchLg,
  Table,
  TextInput,
  Trash01,
} from "@opendate-app/setlist";

import { toastError } from "../../../shared/toastHelper";
import TableSettings from "../../shared/components/TableSettings";
import useTableColumns from "../../ReportingDashboard/hooks/useTableColumns";
import DateRangeEditor, { formatDateRange } from "../shared/DateRangeEditor";
import VenueSelectionModal from "../shared/VenueSelectionModal";
import {
  BROADCAST_COLUMNS,
  BROADCAST_STATUSES,
  DATE_FILTER_KEYS,
  rate,
  timeAgo,
} from "./broadcastIndexConfig";
import CountBadge from "../../shared/components/CountBadge";
import { readListParams, writeListParams } from "../shared/listUrlState";
import { BROADCAST_LIST_DEFAULTS, fromListParams, toListParams } from "./broadcastListState";

const focusBroadcastSearch = () => document.getElementById("broadcast-search");

const qs = require("qs");

const cardStyle = {
  marginTop: 24,
  background: "var(--sl-bg-primary)",
  borderRadius: "12px",
  boxShadow: "0px 1px 2px 0px rgba(10, 13, 18, 0.05)",
  border: "1px solid var(--sl-border-secondary)",
};

const summariseFilter = (filter, values) => {
  if (!values || values.length === 0) return "Any";
  if (DATE_FILTER_KEYS.includes(filter.key)) return formatDateRange(values);
  if (values.length === 1) return filter.options.find((option) => option.value === values[0])?.label ?? values[0];
  return `${values.length} selected`;
};

const BroadcastIndex = ({
  csrfToken,
  team,
  venueOwnerships,
  venueOwnershipId,
  authors,
  timeZone,
}) => {
  const basePath = `/teams/${team.id}/broadcasts`;

  const [broadcasts, setBroadcasts] = useState([]);
  const [pagination, setPagination] = useState({ total_entries: 0, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [initial] = useState(() => fromListParams(readListParams(BROADCAST_LIST_DEFAULTS), venueOwnershipId));
  const [page, setPage] = useState(initial.page);
  const [reloadKey, setReloadKey] = useState(0);
  const [searchInput, setSearchInput] = useState(initial.searchTerm);
  const [searchTerm, setSearchTerm] = useState(initial.searchTerm);
  const [activeFilters, setActiveFilters] = useState(initial.activeFilters);
  const [sort, setSort] = useState(initial.sort);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [showVenueModal, setShowVenueModal] = useState(false);
  const [broadcastToDelete, setBroadcastToDelete] = useState(null);

  const { visibleColumns, hiddenColumns, handleColumnsChange } = useTableColumns({
    columns: BROADCAST_COLUMNS,
    localStorageKey: (id) => `broadcast_columns_v2_${id}`,
    teamId: team.id,
  });

  axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;

  const reload = () => setReloadKey((key) => key + 1);

  useLocalSearchShortcut(focusBroadcastSearch);

  useEffect(() => {
    const handle = setTimeout(() => setSearchTerm(searchInput.trim()), 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const firstRender = useRef(true);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    setPage(1);
  }, [searchTerm, activeFilters, sort]);

  useEffect(() => {
    writeListParams(basePath, toListParams({ searchTerm, activeFilters, page, sort }, venueOwnershipId), BROADCAST_LIST_DEFAULTS);
  }, [searchTerm, activeFilters, page, sort]);

  useEffect(() => {
    let ignore = false;
    setLoading(true);

    axios.get(`${basePath}.json`, {
      params: { page, sort: `${sort.key}_${sort.direction}`, filters: { ...activeFilters, search: searchTerm } },
      paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "brackets" }),
    }).then(({ data }) => {
      if (ignore) return;
      setBroadcasts(data.broadcasts);
      setPagination(data.pagination);
    }).catch(() => {
      if (!ignore) toastError("Could not load broadcasts, please try again");
    }).finally(() => {
      if (!ignore) setLoading(false);
    });

    return () => { ignore = true; };
  }, [page, searchTerm, activeFilters, sort, reloadKey]);

  const sendingIds = broadcasts.filter((broadcast) => broadcast.metrics.sending).map((broadcast) => broadcast.id).join(",");

  useEffect(() => {
    if (!sendingIds) return undefined;
    const ids = sendingIds.split(",");

    const timer = setInterval(async () => {
      const results = await Promise.allSettled(ids.map((id) => axios.get(`${basePath}/${id}/sending_progress`)));
      const progress = {};
      results.forEach((result, index) => {
        if (result.status === "fulfilled") progress[ids[index]] = result.value.data;
      });

      if (Object.values(progress).some((metrics) => !metrics.sending)) {
        reload();
        return;
      }
      setBroadcasts((previous) => previous.map((broadcast) => (
        progress[broadcast.id] ? { ...broadcast, metrics: progress[broadcast.id] } : broadcast
      )));
    }, 2000);

    return () => clearInterval(timer);
  }, [sendingIds]);

  const formatDate = (iso) => new Date(iso).toLocaleDateString("en-US", { timeZone, month: "short", day: "numeric", year: "numeric" });

  const duplicateBroadcast = async (broadcast) => {
    try {
      await axios.post(`${basePath}/${broadcast.id}/duplicate`);
      reload();
    } catch (_error) {
      toastError("Could not duplicate broadcast, please try again");
    }
  };

  const deleteBroadcast = async () => {
    try {
      await axios.delete(`${basePath}/${broadcastToDelete.id}`);
      setBroadcastToDelete(null);
      reload();
    } catch (_error) {
      toastError("Could not delete broadcast, please try again");
    }
  };

  const handleNewBroadcast = () => {
    const pickedVenues = activeFilters.venue || [];
    const newVenueOwnershipId = pickedVenues.length === 1 ? pickedVenues[0] : venueOwnerships.length === 1 && venueOwnerships[0].id;

    if (newVenueOwnershipId) {
      window.location.href = `${basePath}/new?venue_ownership_id=${newVenueOwnershipId}`;
    } else {
      setShowVenueModal(true);
    }
  };

  const nowrap = (content) => <span style={{ whiteSpace: "nowrap" }}>{content}</span>;
  const hasSent = (broadcast) => broadcast.status === "sent" || broadcast.metrics.sending;
  const metric = (render) => ({
    align: "right",
    render: (broadcast) => nowrap(hasSent(broadcast) ? render(broadcast) : "—"),
  });
  const smsDash = (render) => metric((broadcast) => (broadcast.sms ? "—" : render(broadcast)));
  const rateCell = (countKey) => (broadcast) => (
    <div style={{ textAlign: "right" }}>
      <CellText
        primary={rate(broadcast.metrics[countKey], broadcast.metrics.delivered_count)}
        supporting={broadcast.metrics[countKey].toLocaleString()}
      />
    </div>
  );

  const formatTime = (iso) => new Date(iso).toLocaleTimeString("en-US", {
    timeZone, hour: "numeric", minute: "2-digit", timeZoneName: "short",
  });
  const dateCell = (iso, supporting) => (
    <div style={{ whiteSpace: "nowrap" }}>
      <CellText primary={formatDate(iso)} supporting={supporting} />
    </div>
  );

  const cells = {
    name: {
      primary: true,
      render: (broadcast) => {
        const Icon = broadcast.sms ? MessageSquare01 : Mail01;
        return (
          <a
            href={`${basePath}/${broadcast.id}/edit`}
            className="d-flex align-items-center text-reset text-decoration-none"
            style={{ gap: 12, minWidth: 280 }}
          >
            <Icon width={18} height={18} style={{ flexShrink: 0, color: "var(--sl-text-tertiary)" }} aria-label={broadcast.sms ? "SMS" : "Email"} />
            <CellText primary={broadcast.name} supporting={`Last edited ${timeAgo(broadcast.last_edited_at)}`} />
          </a>
        );
      },
    },
    status: {
      render: (broadcast) => {
        const status = BROADCAST_STATUSES[broadcast.metrics.sending ? "sending" : broadcast.status];
        return nowrap(<Badge color={status.color} dot>{status.label}</Badge>);
      },
    },
    sent: {
      sortable: true,
      render: (broadcast) => {
        if (broadcast.metrics.sending) return nowrap("Sending…");
        if (broadcast.sent_at) return dateCell(broadcast.sent_at, formatTime(broadcast.sent_at));
        if (broadcast.scheduled_at) return dateCell(broadcast.scheduled_at, `Scheduled for ${formatTime(broadcast.scheduled_at)}`);
        return "—";
      },
    },
    created: { sortable: true, render: (broadcast) => nowrap(formatDate(broadcast.created_at)) },
    author: { render: (broadcast) => nowrap(broadcast.author_name || "—") },
    queued: metric((broadcast) => broadcast.metrics.queued_count.toLocaleString()),
    failed: metric((broadcast) => (
      <span style={{ color: broadcast.metrics.failed_count > 0 ? "var(--sl-text-error)" : undefined }}>
        {broadcast.metrics.failed_count.toLocaleString()}
      </span>
    )),
    delivered: metric((broadcast) => broadcast.metrics.delivered_count.toLocaleString()),
    opened: smsDash(rateCell("opened_count")),
    clicked: metric(rateCell("clicked_count")),
    converted: metric(rateCell("converted_count")),
    bounced: smsDash(rateCell("bounced_count")),
    suppressed: smsDash(rateCell("suppressed_count")),
    actions: {
      width: "48px",
      render: (broadcast) => (
        <Menu
          placement="bottom-end"
          trigger={<Button variant="tertiary" size="sm" icon={DotsVertical} aria-label="Broadcast actions" />}
          items={[
            { label: "Duplicate", icon: Copy01, onClick: () => duplicateBroadcast(broadcast) },
            { label: "Delete", icon: Trash01, destructive: true, onClick: () => setBroadcastToDelete(broadcast) },
          ]}
        />
      ),
    },
  };

  const tableColumns = visibleColumns.map((column) => ({
    key: column.id,
    header: column.id === "actions" ? "" : column.label,
    ...cells[column.id],
  }));

  const filters = useMemo(() => [
    {
      key: "venue",
      label: "Venue",
      type: "search",
      options: venueOwnerships.map((ownership) => ({ value: String(ownership.id), label: ownership.name })),
    },
    {
      key: "status",
      label: "Status",
      type: "checkbox",
      options: Object.entries(BROADCAST_STATUSES).map(([value, { label }]) => ({ value, label })),
    },
    { key: "created", label: "Created date", render: (props) => <DateRangeEditor {...props} /> },
    { key: "sent", label: "Sent date", render: (props) => <DateRangeEditor {...props} /> },
    { key: "author", label: "Author", type: "search", options: authors },
  ], [venueOwnerships, authors]);

  const count = pagination.total_entries;

  return (
    <div>
      <PageHeader
        title={(
          <span className="d-inline-flex align-items-center" style={{ gap: 8 }}>
            Broadcasts
            <CountBadge count={count} />
          </span>
        )}
        actions={<Button variant="primary" icon={Plus} onClick={handleNewBroadcast}>New Broadcast</Button>}
      />
      <div className="overflow-hidden" style={cardStyle}>
        <div className="px-4 py-3 d-flex align-items-start" style={{ gap: 12 }}>
          <div className="flex-grow-1">
            <FilterBar
              variant="plain"
              filters={filters}
              values={activeFilters}
              onChange={setActiveFilters}
              alwaysShow={["venue"]}
              formatValue={summariseFilter}
              onClearAll={() => setActiveFilters({})}
              resultCount={`${count.toLocaleString()} broadcast${count === 1 ? "" : "s"}`}
              search={(
                <TextInput
                  id="broadcast-search"
                  size="sm"
                  leadingIcon={SearchLg}
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  placeholder="Search broadcasts"
                  aria-label="Search broadcasts"
                />
              )}
            />
          </div>
          <div className="position-relative flex-shrink-0">
            <Button size="sm" icon={Columns03} onClick={() => setSettingsOpen((open) => !open)}>Columns</Button>
            <TableSettings
              isOpen={settingsOpen}
              onClose={() => setSettingsOpen(false)}
              visibleFields={visibleColumns}
              hiddenFields={hiddenColumns}
              onFieldsChange={handleColumnsChange}
            />
          </div>
        </div>

        <div style={{ padding: "4px 8px" }}>
          <Table
            columns={tableColumns}
            data={broadcasts}
            getRowKey={(broadcast) => broadcast.id}
            loading={loading}
            sort={sort}
            onSortChange={(key, direction) => setSort({ key, direction })}
            loadingLabel="Loading broadcasts"
            stickyLastColumn
            empty={(
              <EmptyState
                size="sm"
                icon={SearchLg}
                title="No broadcasts found"
                description="Try different filters, or create a new broadcast."
                primaryAction={{ label: "New Broadcast", icon: Plus, onClick: handleNewBroadcast }}
              />
            )}
          />
        </div>

        {pagination.total_pages > 1 && (
          <div className="px-4 py-3 border-top">
            <Pagination currentPage={page} totalPages={pagination.total_pages} onPageChange={setPage} />
          </div>
        )}

        <VenueSelectionModal
          isOpen={showVenueModal}
          onClose={() => setShowVenueModal(false)}
          onConfirm={(id) => { window.location.href = `${basePath}/new?venue_ownership_id=${id}`; }}
          venueOwnerships={venueOwnerships}
          title="Select a Venue"
          subtitle="Choose a venue to associate with this broadcast"
        />

        <Modal
          open={!!broadcastToDelete}
          onClose={() => setBroadcastToDelete(null)}
          type="error"
          title="Delete broadcast?"
          description="Are you sure you want to delete this Broadcast? This can't be undone."
          footer={(
            <>
              <Button variant="secondary" onClick={() => setBroadcastToDelete(null)}>Cancel</Button>
              <Button variant="destructive" onClick={deleteBroadcast}>Delete</Button>
            </>
          )}
        />
      </div>
    </div>
  );
};

export default BroadcastIndex;
